import { $API } from "boot/api";

export async function fetchCart({ commit }) {
  commit("CLEAR_SINGLE_ORDER");

  commit("SET_LOADING", true);
  try {
    const response = await $API.CART.GET();
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit("SET_ERROR", error.response?.data || "Failed to fetch cart");
    console.error("fetchCart error:", error);
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function addItemToCart({ commit, dispatch }, itemData) {
  commit("CLEAR_SINGLE_ORDER");

  commit("SET_LOADING", true);
  try {
    const response = await $API.CART.POST("items", itemData);
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit("SET_ERROR", error.response?.data || "Failed to add item to cart");
    console.error("addItemToCart error:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function quickAddItemToCart({ commit, dispatch }, itemData) {
  commit("CLEAR_SINGLE_ORDER");
  commit("SET_LOADING", true);

  try {
    const response = await $API.CART.POST("items", {
      ...itemData,
    });
    commit("SET_CART_DATA", response.data);
    return response.data;
  } catch (error) {
    commit("SET_ERROR", error.response?.data || "Failed to add item to cart");
    console.error("quickAddItemToCart error:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function updateCartItem(
  { commit, dispatch },
  { cartItemId, updateData , silent = false }
) {
  if (!silent) commit("SET_LOADING", true);

  try {
    const response = await $API.CART.PUT(`items/${cartItemId}`, updateData);
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit("SET_ERROR", error.response?.data || "Failed to update cart item");
    console.error("updateCartItem error:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function removeItemFromCart({ commit, dispatch }, cartItemId) {
  commit("SET_LOADING", true);
  try {
    const response = await $API.CART.DELETE(`items/${cartItemId}`);
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit(
      "SET_ERROR",
      error.response?.data || "Failed to remove item from cart"
    );
    console.error("removeItemFromCart error:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function clearCart({ commit }) {
  commit("SET_LOADING", true);
  try {
    const response = await $API.CART.DELETE();
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit("SET_ERROR", error.response?.data || "Failed to clear cart");
    console.error("clearCart error:", error);
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function updateCartLicense({ commit }, licenseId) {
  commit("SET_LOADING", true);
  try {
    const response = await $API.CART.PUT("license", { license_id: licenseId });
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit(
      "SET_ERROR",
      error.response?.data || "Failed to update cart license"
    );
    console.error("updateCartLicense error:", error);
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function checkoutCart({ commit, dispatch, state }, payload = {}) {
  commit("SET_LOADING", true);
  try {
    const response = await $API.CART.POST("checkout", payload);

    await dispatch("fetchCart");

    return response;
  } catch (error) {
    if (error.response && error.response.status === 422) {
      commit("SET_ERROR", {
        message: error.response.data.message || "Checkout validation failed.",
        items: error.response.data.errors?.items || [],
      });
    } else {
      commit("SET_ERROR", error.response?.data || "Checkout failed");
    }
    console.error("checkoutCart error:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

export function clearAllData({ dispatch }) {
  dispatch("clearCart");
}

// Single order item actions
export async function setSingleOrderItem({ commit }, orderItem) {
  commit("SET_SINGLE_ORDER_ITEM", orderItem);
}

export function clearSingleOrder({ commit }) {
  commit("CLEAR_SINGLE_ORDER");
}

export function addExternalPoItem({ commit }, item) {
  const externalVendorId = Number(item?.external_vendor_id || 0);
  const externalVendorProductId = Number(item?.external_vendor_product_id || 0);
  const unitType = item?.unit_type || "unit";
  if (!externalVendorId || !externalVendorProductId) {
    throw new Error("Missing external vendor/product ids");
  }

  const id = `${externalVendorId}:${externalVendorProductId}:${unitType}`;
  commit("UPSERT_EXTERNAL_PO_ITEM", {
    ...item,
    id,
    external_vendor_id: externalVendorId,
    external_vendor_product_id: externalVendorProductId,
  });
}

export function updateExternalPoItemQuantity({ commit }, { id, quantity }) {
  commit("UPDATE_EXTERNAL_PO_ITEM_QTY", { id, quantity });
}

export function removeExternalPoItem({ commit }, id) {
  commit("REMOVE_EXTERNAL_PO_ITEM", id);
}

export function clearExternalPoItems({ commit }) {
  commit("CLEAR_EXTERNAL_PO_ITEMS");
}

export function buildExternalPoPayloadForVendor(
  vendorId,
  vendorItems,
  buyerLicenseId
) {
  const safeVendorId = Number(vendorId || 0);
  const safeBuyerLicenseId = Number(buyerLicenseId || 0);
  const sourceItems = Array.isArray(vendorItems) ? vendorItems : [];

  const mergedProductsMap = sourceItems.reduce((acc, item) => {
    const externalVendorProductId = Number(item.external_vendor_product_id || 0);
    if (!externalVendorProductId) return acc;

    const unitCost = Number(item.unit_cost || 0);
    const key = `${externalVendorProductId}:${unitCost}`;
    if (!acc[key]) {
      acc[key] = {
        external_vendor_product_id: externalVendorProductId,
        unit_cost: unitCost,
        allocationsMap: {},
      };
    }

    const incomingAllocations = Array.isArray(item.allocations)
      ? item.allocations
      : [];
    const fallbackQty = Number(item.quantity || 0);
    const safeAllocations = incomingAllocations.length
      ? incomingAllocations
      : [{ buyer_license_id: safeBuyerLicenseId, qty_ordered: fallbackQty }];

    safeAllocations.forEach((allocation) => {
      const licenseId = Number(
        allocation?.buyer_license_id || allocation?.license_id || 0
      );
      const qtyOrdered = Number(
        allocation?.qty_ordered ?? allocation?.quantity ?? 0
      );
      if (!licenseId || qtyOrdered <= 0) return;

      acc[key].allocationsMap[licenseId] =
        Number(acc[key].allocationsMap[licenseId] || 0) + qtyOrdered;
    });

    return acc;
  }, {});

  const products = Object.values(mergedProductsMap)
    .map((product) => {
      const allocations = Object.keys(product.allocationsMap)
        .map((licenseId) => ({
          buyer_license_id: Number(licenseId),
          qty_ordered: Number(product.allocationsMap[licenseId] || 0),
        }))
        .filter((a) => a.buyer_license_id && a.qty_ordered > 0);

      if (!allocations.length) return null;

      const next = {
        external_vendor_product_id: product.external_vendor_product_id,
        allocations,
      };

      if (Number.isFinite(product.unit_cost) && product.unit_cost >= 0) {
        next.unit_cost = product.unit_cost;
      }

      return next;
    })
    .filter(Boolean);

  const vendorMeta = sourceItems[0] || {};

  return {
    external_vendor_id: safeVendorId,
    source: "manual",
    expected_date: vendorMeta.expected_date || null,
    internal_notes: vendorMeta.internal_notes || null,
    vendor_notes: vendorMeta.vendor_notes || null,
    products,
  };
}

export async function submitExternalPoItems({ state, commit, rootState }) {
  const items = Array.isArray(state.externalPoItems) ? state.externalPoItems : [];
  if (!items.length) return { created: [], failed: [] };
  const fallbackCurrentLicenseId = Number(rootState?.current?.license?.id || 0);
  const buyerLicenseId = Number(state.licenseId || fallbackCurrentLicenseId || 0);
  if (!buyerLicenseId) {
    throw new Error("Please select a purchasing license before creating a PO.");
  }

  const groupedByVendor = items.reduce((acc, item) => {
    const vendorId = Number(item.external_vendor_id || 0);
    if (!vendorId) return acc;
    if (!acc[vendorId]) acc[vendorId] = [];
    acc[vendorId].push(item);
    return acc;
  }, {});

  const created = [];
  const failed = [];

  for (const vendorIdStr of Object.keys(groupedByVendor)) {
    const vendorId = Number(vendorIdStr);
    const vendorItems = groupedByVendor[vendorId] || [];
    const payload = buildExternalPoPayloadForVendor(
      vendorId,
      vendorItems,
      buyerLicenseId
    );

    if (!payload.products?.length) {
      failed.push({
        vendor_id: vendorId,
        message: "No valid product allocations found for vendor.",
      });
      continue;
    }

    try {
      const { data } = await $API.PURCHASE_ORDERS.POST("", payload);
      created.push({ vendor_id: vendorId, data });
    } catch (error) {
      failed.push({
        vendor_id: vendorId,
        message: error?.response?.data?.message || error?.message || "Failed to create PO",
      });
    }
  }

  if (!failed.length) {
    commit("CLEAR_EXTERNAL_PO_ITEMS");
    return { created, failed };
  }

  if (created.length) {
    const failedVendorIds = new Set(failed.map((f) => Number(f.vendor_id)));
    const remaining = items.filter(
      (i) => failedVendorIds.has(Number(i.external_vendor_id || 0))
    );
    commit("CLEAR_EXTERNAL_PO_ITEMS");
    remaining.forEach((item) => commit("UPSERT_EXTERNAL_PO_ITEM", item));
  }

  return { created, failed };
}

// New checkout action for single items
export async function checkoutSingleItem(
  { state, dispatch, commit },
  payload = {}
) {
  if (!state.singleOrderItem) {
    throw new Error("No single order item found");
  }

  commit("SET_LOADING", true);

  try {
    const itemId = state.singleOrderItem.id;
    const vendorNoteObj = (payload.vendor_notes || []).find(
      (note) => note.id === itemId
    );
    const vendorNotes = vendorNoteObj ? vendorNoteObj.vendor_note : null;

    // Use the bulk-offers endpoint directly
    const orderData = {
      license: state.licenseId, // Use existing cart license?
      vendor_order_note: payload.vendor_order_note || "",
      offers: [
        {
          inventory_listing: state.singleOrderItem.inventory_listing_id,
          inventory_value: state.singleOrderItem.quantity,
          inventory_unit: state.singleOrderItem.inventory_unit,
          target_price: state.singleOrderItem.unit_price,
          max_price: state.singleOrderItem.unit_price,
          payment_term: state.singleOrderItem.payment_term_id,
          is_sample: state.singleOrderItem.is_sample || false,
          prepackaged_qty: state.singleOrderItem.prepackaged_qty || null,
          ...(state.singleOrderItem.variation_id && {
            variation_id: state.singleOrderItem.variation_id,
          }),
          applied_discounts: state.singleOrderItem.applied_discounts || [],
          vendor_note: vendorNotes,
        },
      ],
    };

    const response = await $API.INVENTORY_LISTINGS.POST(
      "bulk-offers",
      orderData
    );

    dispatch("clearSingleOrder");

    return response;
  } catch (error) {
    if (error.response && error.response.status === 422) {
      commit("SET_ERROR", {
        message: error.response.data.message || "Order validation failed.",
        items: error.response.data.errors?.items || [],
      });
    } else {
      commit("SET_ERROR", error.response?.data || "Order failed");
    }
    console.error("checkoutSingleItem error:", error);
    throw error;
  } finally {
    commit("SET_LOADING", false);
  }
}

export async function updateCartItemQuantity(
  { commit },
  { cartItemId, updateData }
) {
  try {
    const response = await $API.CART.PUT(`items/${cartItemId}`, updateData);
    commit("SET_CART_DATA", response.data);
  } catch (error) {
    commit("SET_ERROR", error.response?.data || "Failed to update cart item");
    console.error("updateCartItemQuantity error:", error);
    throw error;
  }
}
